import { useTranslation } from "react-i18next";

import { Table } from "antd";
import { ColumnsType } from "antd/lib/table";
import { useEffect } from "react";
import { Outlet, useNavigate, useParams } from "react-router-dom";
import { DoctorResponseDto, UserAccountDto } from "../api/client-axios";
import { useAppDispatch, useAppSelector } from "../app/hooks";
import { RootState } from "../app/store";
import { clearErrorDoctors, deleteDoctor, fetchDoctors, selectDoctor, startDeleteDoctor, updateSearchTerm } from "../features/doctorsSlice";
import { RouteNames } from "../routes";
import { formatPhone } from "../utils/formatPhone";
import {initDoctorState} from "../features/doctorSlice";

import { CrudModals } from "../components/CrudModals";
import { CrudToolbar } from "../components/CrudToolbar";
import { EmptyState } from "../components/EmptyState";
import { TableSkeleton } from "../components/TableSkeleton";

export const DoctorsPage = (props: {
    organizationId: string
}) => {
    const dispatch = useAppDispatch();
    const { t } = useTranslation();
    const doctors = useAppSelector((state: RootState) => state.doctors.doctors);
    const selectedDoctor = useAppSelector((state: RootState) => state.doctors.selectedDoctor);
    const searchTerm = useAppSelector((state: RootState) => state.doctors.searchTerm);
    const countries = useAppSelector((state: RootState) => state.country.countries);
    const status = useAppSelector((state: RootState) => state.doctors.status);
    const error = useAppSelector((state: RootState) => state.doctors.error);
    const navigate = useNavigate();
    const params = useParams();

    useEffect(() => {
        if (status === 'idle') {
            dispatch(fetchDoctors({ organizationId: props.organizationId }))
        }

    });

    useEffect(() => {
        if (status === "saved") {
            dispatch(fetchDoctors({organizationId: props.organizationId}));
            dispatch(initDoctorState());
            navigate(-1);
        }
    }, [status])

    if (params.doctorId !== undefined) {
        return <Outlet />
    }

    const onWarnDeleteDoctor = () => {
        if (selectedDoctor != null) {
            dispatch(startDeleteDoctor());
        }
    }

    const onAddDoctor = () => {
        navigate(`/${RouteNames.organizations}/${props.organizationId}/doctors/new`);
    }

    const onEditDoctor = () => {
        if (selectedDoctor != null) {
            navigate(`/${RouteNames.organizations}/${props.organizationId}/doctors/${selectedDoctor.id}`);
        }
    }

    const onReload = () => {

        dispatch(fetchDoctors({ organizationId: props.organizationId }));
    }

    const onSearch = () => {
        dispatch(fetchDoctors({ organizationId: props.organizationId, q: searchTerm }));
    }

    const onRow = (doctor: DoctorResponseDto, index: number | undefined) => {
        return {
            onClick: () => {
                dispatch(selectDoctor(doctor))
            }, // click row
        }
    }

    const onDeleteOrganizationAdmin = () => {
        if (selectedDoctor != null) {
            dispatch(deleteDoctor({
                organizationId: props.organizationId,
                doctorId: selectedDoctor.id!
            }));
        }
    }

    const getRowClassName = (doctor: DoctorResponseDto, index: number) => {
        return selectedDoctor != null && selectedDoctor.id === doctor.id ? 'bg-selected' : '';
    }

    const columns: ColumnsType<DoctorResponseDto> = [
        {

            // width: '100%',
            title: t("column.firstName"),
            dataIndex: 'firstName',
            key: 'firstName',
            sorter: (a, b) => a.firstName! > b.firstName! ? -1 : 1,
        },
        {

            //width: '100%',
            title: t("column.lastName"),
            dataIndex: 'lastName',
            key: 'lastName',
            sorter: (a, b) => a.firstName! > b.firstName! ? -1 : 1,
        },
        {

            //width: '100%',
            title: t("column.email"),
            dataIndex: 'userAccount',
            key: 'email',
            sorter: (a, b) => a.userAccount?.emailAddress! > b.userAccount?.emailAddress! ? -1 : 1,
            render: (userAccount?: UserAccountDto) => userAccount?.emailAddress
        },
        {

            //width: '100%',
            title: t("column.phone"),
            dataIndex: 'userAccount',
            key: 'phone',
            sorter: (a, b) => a.userAccount?.mobileNumber! > b.userAccount?.mobileNumber! ? -1 : 1,
            render: (userAccount?: UserAccountDto) => formatPhone(userAccount?.mobileNumber, countries)
        },

    ];

    //todo implement server side paging and sorting
    const content = status === 'loading' || status === 'idle'
        ? <TableSkeleton />
        : <>
            <CrudToolbar
                hasSelection={selectedDoctor != null}
                canEdit={selectedDoctor?.id != null}
                selectionLabel={[selectedDoctor?.firstName, selectedDoctor?.lastName].filter(Boolean).join(" ")}
                onDelete={onWarnDeleteDoctor}
                onEdit={onEditDoctor}
                onAdd={onAddDoctor}
                onReload={onReload}
                onClearSelection={() => dispatch(selectDoctor(undefined))}
                search={{
                    placeholder: t("placeholder.searchPeople"),
                    value: searchTerm,
                    onChange: value => dispatch(updateSearchTerm(value)),
                    onSearch
                }}
            />
            <div className="card card--flush">
                <Table
                    rowKey={(doctor: DoctorResponseDto) => doctor.id!}
                    rowClassName={getRowClassName}
                    onRow={onRow}
                    dataSource={doctors}
                    columns={columns}
                    locale={{ emptyText: <EmptyState /> }}
                    pagination={{ hideOnSinglePage: true, defaultPageSize: 12, showSizeChanger: false }}
                />
            </div>

            <CrudModals
                status={status}
                error={error}
                entityKey="doctor"
                deleteTarget={[selectedDoctor?.firstName, selectedDoctor?.lastName].filter(Boolean).join(" ")}
                onDismiss={() => dispatch(clearErrorDoctors())}
                onConfirmDelete={onDeleteOrganizationAdmin}
            />
        </>

    return content;
}
